import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';

type StreamingStatusContextValue = {
  getStatus: (threadId: string | null | undefined) => string | null;
  setStatusForThread: (threadId: string | null | undefined, status: string | null) => void;
  /** True when the given thread has at least one active stream() generator.
   *  Stays true during double-texting transitions when isRunning may briefly gap. */
  isThreadStreamActive: (threadId: string | null | undefined) => boolean;
  markStreamActive: (threadId: string) => void;
  markStreamInactive: (threadId: string) => void;
  /** True when the thread is being followed via polling (started elsewhere). */
  isJoinedThread: (threadId: string | null | undefined) => boolean;
  markJoinedThread: (threadId: string) => void;
  unmarkJoinedThread: (threadId: string) => void;
};

const StreamingStatusContext = createContext<StreamingStatusContextValue>({
  getStatus: () => null,
  setStatusForThread: () => {},
  isThreadStreamActive: () => false,
  markStreamActive: () => {},
  markStreamInactive: () => {},
  isJoinedThread: () => false,
  markJoinedThread: () => {},
  unmarkJoinedThread: () => {},
});

export function StreamingStatusProvider({ children }: { children: ReactNode }) {
  const [statusByThread, setStatusByThread] = useState<Record<string, string>>({});
  const [activeStreamsByThread, setActiveStreamsByThread] = useState<Record<string, number>>({});
  const [joinedThreads, setJoinedThreads] = useState<Record<string, boolean>>({});

  const getStatus = useCallback(
    (threadId: string | null | undefined) => {
      if (!threadId) return null;
      return statusByThread[threadId] ?? null;
    },
    [statusByThread],
  );

  const setStatusForThread = useCallback(
    (threadId: string | null | undefined, status: string | null) => {
      if (!threadId) return;
      setStatusByThread((prev) => {
        if (!status) {
          if (!(threadId in prev)) return prev;
          const next = { ...prev };
          delete next[threadId];
          return next;
        }
        if (prev[threadId] === status) return prev;
        return { ...prev, [threadId]: status };
      });
    },
    [],
  );

  const isThreadStreamActive = useCallback(
    (threadId: string | null | undefined) => {
      if (!threadId) return false;
      return (activeStreamsByThread[threadId] ?? 0) > 0;
    },
    [activeStreamsByThread],
  );

  const markStreamActive = useCallback((threadId: string) => {
    setActiveStreamsByThread((prev) => ({
      ...prev,
      [threadId]: (prev[threadId] ?? 0) + 1,
    }));
  }, []);

  const markStreamInactive = useCallback((threadId: string) => {
    setActiveStreamsByThread((prev) => {
      const count = (prev[threadId] ?? 0) - 1;
      if (count <= 0) {
        const next = { ...prev };
        delete next[threadId];
        return next;
      }
      return { ...prev, [threadId]: count };
    });
  }, []);

  const isJoinedThread = useCallback(
    (threadId: string | null | undefined) => {
      if (!threadId) return false;
      return !!joinedThreads[threadId];
    },
    [joinedThreads],
  );

  const markJoinedThread = useCallback((threadId: string) => {
    setJoinedThreads((prev) => (prev[threadId] ? prev : { ...prev, [threadId]: true }));
  }, []);

  const unmarkJoinedThread = useCallback((threadId: string) => {
    setJoinedThreads((prev) => {
      if (!prev[threadId]) return prev;
      const next = { ...prev };
      delete next[threadId];
      return next;
    });
  }, []);

  return (
    <StreamingStatusContext.Provider
      value={{
        getStatus,
        setStatusForThread,
        isThreadStreamActive,
        markStreamActive,
        markStreamInactive,
        isJoinedThread,
        markJoinedThread,
        unmarkJoinedThread,
      }}
    >
      {children}
    </StreamingStatusContext.Provider>
  );
}

export function useStreamingStatus() {
  return useContext(StreamingStatusContext);
}

/**
 * Returns a stable ref-based setter that can be called from async stream
 * callbacks without causing stale-closure issues.
 */
export function useStreamingStatusRef() {
  const { setStatusForThread } = useStreamingStatus();
  const ref = useRef(setStatusForThread);
  useEffect(() => {
    ref.current = setStatusForThread;
  });
  return useCallback((threadId: string | null | undefined, status: string | null) => {
    ref.current(threadId, status);
  }, []);
}

/**
 * Returns stable ref-based mark/unmark for the per-thread active stream
 * tracker, safe to call from async stream generators.
 */
export function useActiveStreamRef() {
  const { markStreamActive, markStreamInactive } = useStreamingStatus();
  const activeRef = useRef(markStreamActive);
  const inactiveRef = useRef(markStreamInactive);
  useEffect(() => {
    activeRef.current = markStreamActive;
  });
  useEffect(() => {
    inactiveRef.current = markStreamInactive;
  });
  return {
    markActive: useCallback((threadId: string) => activeRef.current(threadId), []),
    markInactive: useCallback((threadId: string) => inactiveRef.current(threadId), []),
  };
}

/**
 * Returns stable ref-based mark/unmark for the per-thread "joined" state,
 * safe to call from async stream generators.
 */
export function useJoinedStreamRef() {
  const { markJoinedThread, unmarkJoinedThread } = useStreamingStatus();
  const markRef = useRef(markJoinedThread);
  const unmarkRef = useRef(unmarkJoinedThread);
  useEffect(() => {
    markRef.current = markJoinedThread;
  });
  useEffect(() => {
    unmarkRef.current = unmarkJoinedThread;
  });
  return {
    markJoined: useCallback((threadId: string) => markRef.current(threadId), []),
    unmarkJoined: useCallback((threadId: string) => unmarkRef.current(threadId), []),
  };
}
